'use client';

import { Link } from '@packages/modules/src/Link/Link';
import { ConditionalWrapper } from '@packages/shared/src/ui/ConditionalWrapper/ConditionalWrapper';
import {
  useTrackMarketing,
  useTrackMarketingOnLoad,
} from '@packages/shared/src/hooks/useTrackMarketing/useTrackMarketing';
import type { MouseEventHandler, RefObject } from 'react';
import { useEffect } from 'react';
import { useAssignLinkTargetBasedOnBotStatus } from '../../hooks/useAssignLinkTargetBasedOnBotStatus';
import { buildLinkWithArguments } from '../../utils/buildLinkWithArguments';
import type { CmsImageType } from './types';

const styleJustifyContent = (Align?: string | null) => {
  switch (Align) {
    case 'left':
      return 'flex-start';
    case 'center':
      return 'center';
    case 'right':
      return 'flex-end';
    default:
      return 'unset';
  }
};

const LinkWrapper = ({
  data,
  imgref,
  finalLink,
  trackOnLoad,
  trackClickOnLoad,
}: {
  data: CmsImageType;
  imgref: RefObject<HTMLImageElement | null>;
  finalLink: string;
  trackOnLoad?: boolean;
  trackClickOnLoad: MouseEventHandler<HTMLAnchorElement>;
}) => {
  const { trackClick: trackClickOnIntersect } = useTrackMarketing(
    data,
    imgref,
    finalLink,
    trackOnLoad,
  );
  return (children: any) => (
    <Link
      href={finalLink}
      {...(data.field_link_target && { target: data.field_link_target })}
      {...(data.behavior_paragraph_link_rel && { rel: data.behavior_paragraph_link_rel })}
      onClick={trackOnLoad ? trackClickOnLoad : trackClickOnIntersect}
    >
      {children}
    </Link>
  );
};

export const CmsImageItemWrapper = ({
  children,
  groupOnImage,
  data,
  imgref,
  isVectorImage,
}: {
  children: React.ReactNode;
  groupOnImage: React.ReactNode;
  data: CmsImageType;
  imgref: React.RefObject<HTMLImageElement | null>;
  isVectorImage: boolean;
}) => {
  const {
    field_display_variant: fieldDisplayVariant,
    field_image_size_type: fieldImageSizeType = 'max-width',
    field_alignment: fieldAlignment = 'center',
    field_link: fieldLink,
    trackOnLoad,
  } = data;
  const tmpLink = buildLinkWithArguments(fieldLink, data);
  const assignLinkTarget = useAssignLinkTargetBasedOnBotStatus();
  const finalLink = assignLinkTarget(tmpLink);
  const { trackDisplay, trackClick: trackClickOnLoad } = useTrackMarketingOnLoad(data, finalLink);
  useEffect(() => {
    if (trackOnLoad) {
      trackDisplay();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const styleClasses = [
    'relative flex w-full flex-col',
    '[&_img]:h-auto!',
    isVectorImage ? '[&_img]:block [&_img]:w-full' : null,
    fieldImageSizeType === 'full-width' ? '[&_img]:w-full' : null,
    fieldImageSizeType === 'full-width' || isVectorImage
      ? '[&_img]:max-w-auto'
      : '[&_img]:max-w-full',
    fieldDisplayVariant === 'circle' ? '[&_img]:rounded-full' : null,
  ];

  return (
    <span
      className={styleClasses.join(' ').trim()}
      style={{ alignItems: styleJustifyContent(fieldAlignment) }}
    >
      <ConditionalWrapper
        condition={!!fieldLink}
        wrapper={LinkWrapper({
          data,
          imgref,
          finalLink,
          trackOnLoad,
          trackClickOnLoad,
        })}
      >
        {children}
      </ConditionalWrapper>
      {groupOnImage}
    </span>
  );
};
